import * as React from "react";
import { MessageSquareWarning } from "lucide-react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import { usePortal } from "@/features/portal/portal-context";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { report, type ReportCategory } from "@/lib/report";

const CATEGORIES: { id: ReportCategory; label: string }[] = [
  { id: "bug", label: "Something broke" },
  { id: "confusing", label: "Confusing" },
  { id: "idea", label: "Idea" },
  { id: "other", label: "Other" },
];

/** Best-effort stable session id (feeds the report payload). */
function sessionId(): string {
  try {
    const KEY = "popin.portal.session";
    let id = globalThis.localStorage?.getItem(KEY);
    if (!id) {
      id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      globalThis.localStorage?.setItem(KEY, id);
    }
    return id;
  } catch {
    return "anon";
  }
}

/** Map a route to a coarse surface name for the report. */
function surfaceOf(pathname: string): string {
  if (pathname.startsWith("/admin")) return "admin";
  if (pathname.startsWith("/create")) return "create-brand";
  const m = pathname.match(/\/b\/[^/]+\/([^/?]+)/);
  return m ? m[1]! : "portal";
}

/**
 * "Report an issue" — the one visible feedback affordance in the portal chrome.
 * Everything routes through the {@link report} seam (a local stub sink today; the
 * homebrew Feedback backend + Botler capture is the swap target). Unobtrusive,
 * matches the sidebar's ghost-button convention.
 */
export function ReportIssue() {
  const { user, activeBrand } = usePortal();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const [category, setCategory] = React.useState<ReportCategory>("bug");
  const [message, setMessage] = React.useState("");

  function submit() {
    report({
      surface: surfaceOf(location.pathname),
      route: location.pathname,
      brandId: activeBrand?.id ?? null,
      session: sessionId(),
      user: user.username || user.name || "anonymous",
      category,
      message: message.trim() || undefined,
    });
    setOpen(false);
    setMessage("");
    setCategory("bug");
    toast.success("Thanks — your report was filed.");
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mb-2 flex items-center gap-2 rounded-[10px] px-[11px] py-2 text-[12.5px] font-semibold text-text-3 outline-none transition-colors hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring"
      >
        <MessageSquareWarning className="size-4" /> Report an issue
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report an issue</DialogTitle>
            <DialogDescription>
              Tell us what happened. We include the page you’re on to help us track it down.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2">
              <Label className="text-[13px] font-semibold">Type</Label>
              <div className="flex flex-wrap gap-1.5">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    aria-pressed={category === c.id}
                    onClick={() => setCategory(c.id)}
                    className={cn(
                      "rounded-[10px] border px-3 py-1.5 text-[12.5px] font-semibold outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring",
                      category === c.id
                        ? "border-brand bg-brand/10 text-foreground"
                        : "border-border bg-panel-2 text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="report-message" className="text-[13px] font-semibold">
                Details <span className="font-normal text-text-3">(optional)</span>
              </Label>
              <textarea
                id="report-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                placeholder="What went wrong, or what would make this better?"
                className="w-full resize-none rounded-[10px] border border-border bg-field px-3 py-2.5 text-[13px] outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="subtle" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={submit}>
              Send report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
